// KAN-114: header bell with unread badge and a dropdown of recent
// notifications. Polls every 60s and on window focus — no websockets.
import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Dropdown, List, Typography, theme } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  PortalNotification,
} from '../services/notificationService';

const POLL_MS = 60_000;
const { Text } = Typography;
const { useToken } = theme;

const relative = (iso: string) => {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(iso).toLocaleDateString('en-GB');
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const { token } = useToken();
  const [items, setItems] = useState<PortalNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async (signal?: AbortSignal) => {
    try {
      const res = await getNotifications(20);
      if (signal?.aborted) return;
      setItems(res.notifications);
      setUnread(res.unread);
    } catch (err) {
      // The bell is decoration on every page; never surface a poll failure.
      console.warn('notifications: refresh failed', err);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    refresh(controller.signal);
    const timer = setInterval(() => refresh(controller.signal), POLL_MS);
    const onFocus = () => refresh(controller.signal);
    window.addEventListener('focus', onFocus);
    return () => {
      controller.abort();
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  const onClickItem = async (n: PortalNotification) => {
    setOpen(false);
    if (!n.readAt) {
      setItems(prev =>
        prev.map(i => (i.id === n.id ? { ...i, readAt: new Date().toISOString() } : i))
      );
      setUnread(u => Math.max(0, u - 1));
      markNotificationRead(n.id).catch(err => console.warn('notifications: mark read failed', err));
    }
    if (n.link) navigate(n.link);
  };

  const onReadAll = async () => {
    setItems(prev => prev.map(i => ({ ...i, readAt: i.readAt || new Date().toISOString() })));
    setUnread(0);
    markAllNotificationsRead().catch(err => console.warn('notifications: read-all failed', err));
  };

  const panel = (
    <div
      style={{
        width: 360,
        maxWidth: 'calc(100vw - 24px)',
        background: token.colorBgElevated,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadowSecondary,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <Text strong>Notifications</Text>
        <Button type="link" size="small" disabled={unread === 0} onClick={onReadAll}>
          Mark all read
        </Button>
      </div>
      <List
        size="small"
        dataSource={items}
        locale={{ emptyText: 'Nothing yet' }}
        style={{ maxHeight: 400, overflowY: 'auto' }}
        renderItem={n => (
          <List.Item
            onClick={() => onClickItem(n)}
            style={{
              cursor: n.link ? 'pointer' : 'default',
              padding: '8px 12px',
              background: n.readAt ? undefined : token.colorPrimaryBg,
            }}
          >
            <List.Item.Meta
              title={<Text strong={!n.readAt}>{n.title}</Text>}
              description={
                <>
                  {n.body && <div>{n.body}</div>}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {relative(n.createdAt)}
                  </Text>
                </>
              }
            />
          </List.Item>
        )}
      />
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      placement="bottomRight"
      popupRender={() => panel}
    >
      <Badge count={unread} size="small" overflowCount={99}>
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: 18 }} />}
          aria-label={`Notifications, ${unread} unread`}
        />
      </Badge>
    </Dropdown>
  );
};

export default NotificationBell;
